import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { PageHead } from "@/components/utils";
import { FadeUp, ClipImage } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { BRIDAL_PACKAGES, GROOM_PACKAGE, IMAGES, BRIDE_GALLERY } from "@/lib/data";

export default function Bridal() {
  return (
    <>
      <PageHead
        title="Bridal Makeup & Packages — Good Looks Salon, Mansa"
        description="Bridal, reception, engagement and groom packages at Good Looks Salon, Sunni Gali, Mansa. Studio and on-site bridal services."
      />

      <section className="relative bg-[#1B1B1B] text-[#F3EAD9] pt-40 pb-24 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 opacity-50">
          <img src={IMAGES.bridalWide} alt="Bridal look" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1B1B1B]/60 to-[#1B1B1B]" />
        </div>
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
          <FadeUp>
            <p className="eyebrow text-[#C89B3C] mb-6">— Bridal chapter</p>
            <h1 className="font-display text-5xl md:text-8xl leading-[0.95] max-w-5xl">
              Made for<br /><span className="italic text-[#C89B3C]">the aisle.</span>
            </h1>
            <p className="mt-8 text-white/75 max-w-xl leading-relaxed">
              From engagement to reception, our bridal packages are planned
              across sittings so you arrive rested, radiant and unmistakably
              yourself. Studio and on-site options available.
            </p>
          </FadeUp>
        </div>
      </section>

      <Marquee items={["Bridal", "Engagement", "Reception", "MAC", "NARS", "Studio"]} className="bg-[#F3EAD9] text-[#1B1B1B]" />

      <section className="bg-[#F3EAD9] text-[#1B1B1B] py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <FadeUp>
              <p className="eyebrow text-[#C89B3C] mb-4">— Brides of Good Looks</p>
              <h2 className="font-display text-4xl md:text-6xl leading-[0.98]">
                North Indian<br /><span className="italic">bridal edits.</span>
              </h2>
              <p className="mt-4 text-black/60 max-w-lg text-sm italic">
                Look-book of moods & references — replaced with real Good Looks
                brides as our portfolio grows.
              </p>
            </FadeUp>
            <FadeUp delay={0.1}>
              <Link to="/book" className="btn-outline-dark">
                <span>Book bridal trial</span>
                <ArrowUpRight size={16} />
              </Link>
            </FadeUp>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
            {BRIDE_GALLERY.map((src, i) => {
              const tall = i === 0 || i === 4;
              return (
                <FadeUp key={i} delay={(i % 3) * 0.05} className={tall ? "row-span-2" : ""}>
                  <div className={`overflow-hidden bg-black/5 ${tall ? "aspect-[3/5]" : "aspect-[4/5]"}`}>
                    <img
                      src={src}
                      alt={`North Indian bride reference ${i + 1}`}
                      loading="lazy"
                      className="w-full h-full object-cover hover:scale-[1.05] transition-transform duration-[1400ms]"
                    />
                  </div>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[#F3EAD9] text-[#1B1B1B] py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <FadeUp>
              <p className="eyebrow text-[#C89B3C] mb-4">— Signature packages</p>
              <h2 className="font-display text-4xl md:text-6xl leading-[0.98]">
                Three ways<br /><span className="italic">to become a bride.</span>
              </h2>
            </FadeUp>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {BRIDAL_PACKAGES.map((pkg, i) => {
              const highlight = i === 2;
              return (
                <FadeUp key={pkg.name} delay={i * 0.08}>
                  <div
                    className={`h-full flex flex-col border p-8 md:p-10 transition-colors ${
                      highlight
                        ? "bg-[#1B1B1B] text-[#F3EAD9] border-[#C89B3C]"
                        : "bg-white border-black/10 hover:border-[#C89B3C]"
                    }`}
                  >
                    <div className="flex items-baseline justify-between mb-6">
                      <span className={`eyebrow ${highlight ? "text-[#C89B3C]" : "text-black/50"}`}>
                        0{i + 1} · Package
                      </span>
                      {highlight && (
                        <span className="text-[10px] tracking-widest uppercase text-[#C89B3C]">
                          Signature
                        </span>
                      )}
                    </div>
                    <h3 className="font-display text-3xl md:text-4xl leading-tight">{pkg.name}</h3>
                    <p className={`font-display italic text-4xl mt-3 ${highlight ? "text-[#C89B3C]" : "text-[#C89B3C]"}`}>
                      {pkg.price}
                    </p>
                    <div className={`mt-6 pt-6 border-t ${highlight ? "border-white/15" : "border-black/10"} flex-1`}>
                      <ul className="space-y-3">
                        {pkg.items.map((it) => (
                          <li key={it} className="flex items-start gap-3 text-sm">
                            <Check
                              size={14}
                              className="mt-1 text-[#C89B3C] shrink-0"
                              strokeWidth={2}
                            />
                            <span className={highlight ? "text-white/80" : "text-black/70"}>
                              {it}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Link
                      to="/book"
                      className={`mt-8 inline-flex items-center gap-2 text-sm font-medium tracking-wide gl-link ${
                        highlight ? "text-[#C89B3C]" : "text-[#1B1B1B]"
                      }`}
                    >
                      Enquire about this package <ArrowUpRight size={16} />
                    </Link>
                  </div>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[#F3EAD9] py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <FadeUp className="lg:col-span-5">
            <ClipImage src={IMAGES.bridal} alt="Groom package" className="aspect-[3/4]" />
          </FadeUp>
          <div className="lg:col-span-7 lg:pl-6">
            <FadeUp>
              <p className="eyebrow text-[#C89B3C] mb-4">— For the groom</p>
              <h2 className="font-display text-4xl md:text-6xl leading-[0.98]">
                {GROOM_PACKAGE.name}<br /><span className="italic">across two sittings.</span>
              </h2>
            </FadeUp>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
              {GROOM_PACKAGE.sittings.map((s, i) => (
                <FadeUp key={s.label} delay={i * 0.06}>
                  <div className="border border-black/10 p-6 md:p-8 bg-white">
                    <p className="chapter-number text-2xl mb-3">0{i + 1}</p>
                    <h3 className="font-display text-2xl mb-4">{s.label}</h3>
                    <ul className="space-y-2 text-sm text-black/70">
                      {s.items.map((it) => (
                        <li key={it} className="flex items-start gap-2">
                          <span className="text-[#C89B3C]">—</span>
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </FadeUp>
              ))}
            </div>
            <div className="mt-10">
              <Link to="/book" className="btn-outline-dark">
                <span>Book bridal consultation</span>
                <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
